import { useState, type MouseEvent } from 'react';
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { buildSchoolReportHref } from '@/config/paths';
import { SCHOOL_ID_LENGTH } from '@/constants/schoolConstants';

interface DashboardHeaderProps {
  schoolId?: string;
  disabled?: boolean;
  generateSchoolReportDisabled?: boolean;
  onSchoolReport?: () => void;
  onExportCsv?: () => void;
  onGenerateSchoolReport: () => void;
  onExportPdf?: () => void;
}

const outlinedBtnSx = {
  borderColor: 'grey.600',
  backgroundColor: 'grey.100',
  color: 'text.secondary',
  fontSize: '0.8125rem',
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
  '&.Mui-disabled': {
    pointerEvents: 'auto',
    cursor: 'not-allowed',
  },
} as const;

const schoolReportBtnSx = {
  ...outlinedBtnSx,
  borderColor: 'primary.main',
  color: 'primary.main',
  backgroundColor: 'background.paper',
  '&:hover': {
    borderColor: 'primary.dark',
    color: 'primary.dark',
    backgroundColor: 'primary.50',
  },
  '&.Mui-disabled': {
    ...outlinedBtnSx['&.Mui-disabled'],
    borderColor: 'grey.400',
    color: 'grey.500',
    backgroundColor: 'grey.100',
  },
} as const;

export default function DashboardHeader({
  schoolId = '',
  disabled = false,
  generateSchoolReportDisabled = false,
  onExportCsv,
  onGenerateSchoolReport,
  onExportPdf,
}: DashboardHeaderProps) {
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const exportOpen = Boolean(exportAnchor);
  const trimmedSchoolId = schoolId.trim();
  const schoolReportDisabled = trimmedSchoolId.length !== SCHOOL_ID_LENGTH;

  const handleExportToggle = (e: MouseEvent<HTMLButtonElement>) => {
    setExportAnchor((prev) => (prev ? null : e.currentTarget));
  };

  const handleExportClose = () => {
    setExportAnchor(null);
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
      gap={1.5}
      sx={{ mb: 2.5 }}
    >
      <Typography
        variant="h4"
        sx={{
          color: '#0F172A',
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: 'normal',
        }}
      >
        Live Insights Dashboard
      </Typography>

      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          id="school-report-btn"
          variant="outlined"
          size="small"
          disabled={schoolReportDisabled}
          startIcon={<ArticleOutlinedIcon sx={{ fontSize: '15px !important' }} />}
          onClick={() => {
            if (schoolReportDisabled) return;
            window.open(buildSchoolReportHref(trimmedSchoolId), '_blank', 'noopener,noreferrer');
          }}
          sx={schoolReportBtnSx}
        >
          School Report
        </Button>

        <Button
          id="export-btn"
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '15px !important' }} />}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '18px !important' }} />}
          onClick={handleExportToggle}
          aria-controls={exportOpen ? 'export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={exportOpen ? 'true' : undefined}
          sx={outlinedBtnSx}
        >
          Export
        </Button>
        <Menu
          id="export-menu"
          anchorEl={exportAnchor}
          open={exportOpen}
          onClose={handleExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { mt: 0.5, minWidth: 260 },
          }}
        >
          <MenuItem
            id="export-csv-btn"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              handleExportClose();
              onExportCsv?.();
            }}
            sx={{
              fontSize: '0.8125rem',
              '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' },
            }}
          >
            Export Magic Link csv
          </MenuItem>
          <MenuItem
            id="export-pdf-btn"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              handleExportClose();
              onExportPdf?.();
            }}
            sx={{
              fontSize: '0.8125rem',
              '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' },
            }}
          >
            Export School Login Report pdf
          </MenuItem>
        </Menu>

        <Button
          id="generate-school-login-report-btn"
          variant="outlined"
          size="small"
          disabled={generateSchoolReportDisabled}
          onClick={onGenerateSchoolReport}
          sx={outlinedBtnSx}
        >
          Generate School login report
        </Button>
      </Stack>
    </Stack>
  );
}
